<template>
    <div class="sp-comm-control">
        <div class="sp-comm-control__sec-h">Контроль переписки · {{ channelDisplayName }}</div>
        <div class="cc-lab">
            Номер, на который будет подключён контроль переписки по каналу «{{ channelDisplayName }}»:
            <v-tooltip bottom color="#000000DE" max-width="300px">
                <template v-slot:activator="{ on, attrs }">
                    <span class="cc-on-help" v-bind="attrs" v-on="on">?</span>
                </template>
                <span class="white--text">{{ controlPhoneHintText }}</span>
            </v-tooltip>
        </div>

        <div class="sp-comm-control__add-block">
            <div class="cc-add-row">
                <input
                    type="tel"
                    class="dr-input"
                    placeholder="+7 (987) 654-32-10"
                    :value="formattedPhoneToControl"
                    @input="onPhoneInputEvent"
                    @keydown="onPhoneKeydown"
                    @paste="onPhonePaste"
                />
                <v-tooltip
                    bottom
                    color="#000000DE"
                    max-width="300px"
                    :disabled="!controlButtonDisabled || !controlButtonDisabledHint"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <span class="cc-on-btn-wrap" v-bind="attrs" v-on="on">
                            <button
                                type="button"
                                class="cc-on-btn"
                                :disabled="controlButtonDisabled"
                                @click="controlOn"
                            >
                                Вкл. трансляцию
                            </button>
                        </span>
                    </template>
                    <span class="white--text">{{ controlButtonDisabledHint }}</span>
                </v-tooltip>
                <v-tooltip bottom color="#000000DE" max-width="300px">
                    <template v-slot:activator="{ on, attrs }">
                        <span class="cc-on-help" v-bind="attrs" v-on="on">?</span>
                    </template>
                    <span class="white--text">Введите номер и нажмите, чтобы включить трансляцию</span>
                </v-tooltip>
            </div>
            <div v-if="phoneWarning" class="sp-comm-control__phone-warn">
                {{ phoneWarning }}
            </div>
        </div>

        <div v-if="isControlErrorShow" class="sp-comm-control__err">
            <img class="sp-comm-control__err-icon" src="@/assets/icons/error-status.svg" alt="" />
            Номер не найден, пройдите инструкцию выше
        </div>

        <div class="sp-comm-control__sec-h">Трансляция включена на:</div>
        <div class="sp-comm-control__list">
            <StarterPackLoader
                v-if="listLoading"
                compact
                label="Загружаем номера..."
            />
            <div v-else-if="!phoneList.length" class="cc-empty">
                Пока ни на один номер трансляция не включена.
            </div>
            <template v-else>
                <div
                    v-for="phone in phoneList"
                    :key="phone.userId"
                    class="sp-comm-control__phone-item"
                >
                    <div class="sp-comm-control__phone-readonly">
                        {{ formatPhoneNumber(phone.userId) || phone.userId }}
                    </div>
                    <button
                        type="button"
                        class="sp-comm-control__remove"
                        aria-label="Отключить"
                        @click="controlOff(phone.userId)"
                    >
                        <v-icon size="18">mdi-close</v-icon>
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { formatPhoneNumber } from '@/helpers/formatters/phoneFormatter'
import { isValidPhone } from '@/helpers/validators/phoneValidator'
import {
    checkPhoneBeforeActivatingControl,
    controlPhoneList,
    controlPhoneToggle,
} from '@/api/networkFixer'
import StarterPackLoader from './StarterPackLoader.vue'

const CONTROL_PHONE_HINT_TEXT =
    'Все входящии и исходящии сообщения на номере, к которому подключен бот, будут дублироваться в Telegram на номер, который вы укажите для осуществления контроля'

export default {
    name: 'StarterPackCommControlBlock',
    components: { StarterPackLoader },
    props: {
        connectorId: {
            type: String,
            required: true,
        },
        channelDisplayName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            phoneToControl: '',
            phoneList: [],
            phoneWarning: '',
            isControlErrorShow: false,
            controlErrorHideTimer: null,
            loading: false,
            listLoading: false,
        }
    },
    computed: {
        controlPhoneHintText() {
            return CONTROL_PHONE_HINT_TEXT
        },
        project() {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id) || {}
        },
        formattedPhoneToControl() {
            return formatPhoneNumber(this.phoneToControl)
        },
        controlButtonDisabled() {
            const role = this.project?.myRole || 'observer'
            return (
                this.loading ||
                !isValidPhone(this.phoneToControl) ||
                this.phoneList.some(item => this.normalizePhoneInput(item.userId) === this.phoneToControl) ||
                role !== 'owner'
            )
        },
        controlButtonDisabledHint() {
            const role = this.project?.myRole || 'observer'
            if (role !== 'owner') {
                return 'Обратитесь к вашему руководителю для подключения трансляции'
            }
            if (!isValidPhone(this.phoneToControl)) {
                return 'Некорректный номер телефона'
            }
            if (this.phoneList.some(item => this.normalizePhoneInput(item.userId) === this.phoneToControl)) {
                return 'Трансляция уже включена'
            }
            return ''
        },
    },
    beforeDestroy() {
        this.clearControlErrorHideTimer()
    },
    watch: {
        connectorId: {
            immediate: true,
            handler() {
                this.resetInput()
                this.loadPhoneList()
            },
        },
    },
    methods: {
        formatPhoneNumber,
        normalizePhoneInput(value) {
            const str = String(value || '').replace(/[^\d+]/g, '')
            if (!str) return ''
            if (str === '+') return '+'
            const hasPlus = str.startsWith('+')
            const digits = str.replace(/\D/g, '')
            let normalized = hasPlus ? `+${digits}` : digits
            if (!normalized.startsWith('+')) {
                normalized = `+${normalized}`
            }
            return normalized.replace('+8', '+7')
        },
        onPhoneInput(value) {
            const normalized = this.normalizePhoneInput(value)
            this.phoneToControl = normalized
            this.phoneWarning = this.controlPhoneValidator(normalized) || ''
        },
        onPhoneInputEvent(event) {
            this.onPhoneInput(event.target.value)
        },
        onPhoneKeydown(event) {
            if (event.ctrlKey || event.metaKey || event.altKey) {
                return
            }
            const key = event.key
            const allowedControl = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab']
            if (allowedControl.includes(key)) {
                return
            }
            if (key >= '0' && key <= '9') {
                return
            }
            const target = event.target
            const replacesAll =
                target &&
                target.selectionStart === 0 &&
                target.selectionEnd === (target.value || '').length &&
                (target.value || '').length > 0
            if (replacesAll && (key === '+' || key === '@' || key === '-' || /[a-zA-Z]/.test(key))) {
                return
            }
            if (key === '+') {
                const value = String(target?.value || '')
                const start = target?.selectionStart ?? 0
                const hasPlus = value.replace(/[^\d+]/g, '').startsWith('+')
                if ((start === 0 || replacesAll) && !hasPlus) {
                    return
                }
            }
            event.preventDefault()
        },
        onPhonePaste(event) {
            event.preventDefault()
            const pasted = event.clipboardData?.getData('text') || ''
            this.onPhoneInput(pasted.replace(/[^\d+]/g, ''))
        },
        controlPhoneValidator(phone) {
            if (phone && !isValidPhone(phone)) {
                return 'Некорректный номер телефона'
            }
        },
        clearControlErrorHideTimer() {
            if (this.controlErrorHideTimer) {
                clearTimeout(this.controlErrorHideTimer)
                this.controlErrorHideTimer = null
            }
        },
        hideControlError() {
            this.clearControlErrorHideTimer()
            this.isControlErrorShow = false
        },
        showControlError() {
            this.hideControlError()
            this.isControlErrorShow = true
            this.controlErrorHideTimer = setTimeout(() => {
                this.isControlErrorShow = false
                this.controlErrorHideTimer = null
            }, 5000)
        },
        resetInput() {
            this.phoneToControl = ''
            this.phoneWarning = ''
            this.hideControlError()
        },
        emitPhonesUpdated() {
            this.$emit('phones-updated', {
                connectorId: this.connectorId,
                phones: this.phoneList,
            })
        },
        async loadPhoneList() {
            if (!this.connectorId) {
                this.phoneList = []
                this.emitPhonesUpdated()
                return
            }

            const shouldToggleLoader = !this.listLoading
            if (shouldToggleLoader) {
                this.listLoading = true
            }
            try {
                const res = await controlPhoneList({ connectorId: this.connectorId })
                if (!res?.error) {
                    this.phoneList = res?.data || []
                    this.emitPhonesUpdated()
                }
            } catch (e) {
                this.phoneList = []
                this.emitPhonesUpdated()
            } finally {
                if (shouldToggleLoader) {
                    this.listLoading = false
                }
            }
        },
        async controlOn() {
            if (!this.connectorId || this.controlButtonDisabled) {
                return
            }

            const phone = this.phoneToControl.replace('+', '')
            this.hideControlError()
            this.loading = true
            this.listLoading = true

            try {
                const checkRes = await checkPhoneBeforeActivatingControl({
                    phone,
                    connectorId: this.connectorId,
                })
                if (checkRes?.error) {
                    this.$store.dispatch('callNotify', 'Произошла ошибка')
                    return
                }

                if (checkRes.data === false) {
                    this.showControlError()
                    return
                }

                const connectRes = await controlPhoneToggle({
                    connectorId: this.connectorId,
                    phone,
                })
                if (connectRes?.error) {
                    this.$store.dispatch('callNotify', 'Произошла ошибка')
                    return
                }

                await this.loadPhoneList()
                this.resetInput()
            } finally {
                this.loading = false
                this.listLoading = false
            }
        },
        async controlOff(userId) {
            if (!this.connectorId) {
                return
            }

            const phone = String(userId).replace('+', '')
            this.listLoading = true
            try {
                const deConnectRes = await controlPhoneToggle({
                    connectorId: this.connectorId,
                    phone,
                })
                if (deConnectRes?.error) {
                    this.$store.dispatch('callNotify', 'Произошла ошибка')
                    return
                }

                await this.loadPhoneList()
            } finally {
                this.listLoading = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
@import '@/vars.sass';

.sp-comm-control__sec-h {
    margin-bottom: 11px;
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 800;
}

.cc-lab {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 11px;
    color: #2b3350;
    font-size: 12.5px;
    line-height: 1.45;

    .q {
        display: inline-flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        background: $main-new;
        border-radius: 50%;
        cursor: help;
    }
}

.sp-comm-control__add-block {
    margin-bottom: 24px;
}

.cc-add-row {
    display: flex;
    align-items: center;
    gap: 10px;

    .dr-input,
    .cc-on-btn {
        box-sizing: border-box;
    }

    .dr-input {
        height: 42px;
        flex: 1;
        min-width: 0;
        padding: 0 14px;
        line-height: 40px;
    }
}

.dr-input {
    width: 100%;
    margin: 0;
    color: #1a2040;
    font-family: Inter, system-ui, sans-serif;
    font-size: 14px;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 9px;
    outline: none;
    transition: 0.15s;
    appearance: none;

    &::placeholder {
        color: #95a0b2;
    }

    &:focus {
        border-color: $main-new;
        box-shadow: 0 0 0 3px rgba(82, 90, 213, 0.12);
    }
}

.sp-comm-control__phone-warn {
    margin-top: 4px;
    color: #e0353b;
    font-size: 12px;
    line-height: 1.3;
}

.cc-on-btn-wrap {
    display: inline-flex;
    flex-shrink: 0;
}

.cc-on-btn {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: 12px 16px;
    color: #fff;
    background: $main-new;
    border: 1px solid $main-new;
    border-radius: 9px;
    box-shadow: 0 2px 8px rgba(82, 90, 213, 0.26);
    cursor: pointer;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 12.5px;
    font-weight: 700;
    white-space: nowrap;
    transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) {
        background: #454cc0;
        border-color: #454cc0;
        box-shadow: 0 4px 14px rgba(82, 90, 213, 0.34);
        transform: translateY(-1px);
    }

    &:disabled {
        color: #a9afc0;
        background: #e2e4ec;
        border-color: #e3e7ee;
        box-shadow: none;
        cursor: not-allowed;
        transform: none;
    }
}

.cc-on-help {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    background: $main-new;
    border-radius: 50%;
    cursor: help;
}

.sp-comm-control__err {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: -8px 0 16px;
    color: #d93025;
    font-size: 12.5px;
    line-height: 1.4;
}

.sp-comm-control__err-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

.sp-comm-control__list {
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.cc-empty {
    padding: 6px 2px;
    color: $muted-light-new;
    font-size: 12px;
}

.sp-comm-control__phone-item {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
}

.sp-comm-control__phone-readonly {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 44px 12px 14px;
    color: #2a3b51;
    font-family: Inter, system-ui, sans-serif;
    font-size: 14px;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 11px;
}

.sp-comm-control__remove {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    cursor: pointer;
    background: rgba(226, 0, 41, 0.1);
    border: none;
    border-radius: 6px;
    transition: background 0.2s ease, transform 0.2s ease;

    .v-icon {
        color: #e20029 !important;
        opacity: 0.7;
    }

    &:hover {
        background: rgba(226, 0, 41, 0.2);
        transform: translateY(-50%) scale(1.05);

        .v-icon {
            opacity: 1;
        }
    }
}
</style>
